import {
  AbstractType,
  Injectable,
  InjectionToken,
  Injector,
  Type,
} from '@angular/core';
import {
  HttpClient,
  HttpErrorResponse,
  HttpParams,
} from '@angular/common/http';
import {
  isFunction,
  get,
  merge,
  omit,
  upperCase,
  remove,
  isEmpty,
  set,
  toLower,
} from 'lodash';
import {
  Observable,
  observableThrowError,
  rx,
  Subject,
} from '@app/shared/rx/rx-wrapper';
import {
  ICrowdinConfig,
  IErrorOverride,
  IFilterQuery,
} from '@app/shared/types';

import { HttpErrorHandler } from '../base';
import { environment } from '../../../../environments/environment';
import { DEFAULT_LOCALE } from '@config/global-constants';
import { lazyInject } from '@app/shared/decorators';
import { ILocales } from '@app/i18n/i18n.types';
import { SupportedAppCodes } from '@app/shared/types/enums/permission.enum';
import { createRequestParams } from '@app/shared/utils/request.utils';
import { DmDataService } from '../dm-data';
import { STATE_KEYS } from '../state';
import { TranslateService } from '@ngx-translate/core';
import { XprtDataService } from '../xprt-data';
import { I18nService } from './i18n.service';
import { LocaleService } from './locale.service';
import { of } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class TranslationService {
  @lazyInject(I18nService)
  public i18nService: I18nService;
  @lazyInject(DmDataService) public dmDataService: DmDataService;
  @lazyInject(XprtDataService) public xprtDataService: XprtDataService;

  public translationInitialized$: Subject<boolean> = new Subject();

  public loadedLocales: string[] = [];

  public constructor(
    private http: HttpClient,
    public injector: Injector,
    public translateService: TranslateService,
    private localeService: LocaleService,
  ) {}

  protected catchHttpError =
    (handleBeforeThrowError?: () => void, errorOverride?: IErrorOverride) =>
    <T>(input$: Observable<T>): Observable<T> =>
      input$.pipe(
        rx.catchError((httpError: HttpErrorResponse) => {
          if (isFunction(handleBeforeThrowError)) {
            handleBeforeThrowError();
          }

          return observableThrowError(
            HttpErrorHandler.composeError(httpError, {
              errorOverride,
              i18nService: this.i18nService,
            }),
          );
        }),
      );

  protected extractVersionNumber(version: string): string {
    if (!version) return version;

    // Remove BUILD- prefix if exists
    let cleanVersion = version.replace('BUILD-', '');

    // Extract version number from git tag format (e.g., 3.10.233-19-g68f035a -> 3.10.233)
    // Match semantic version pattern at the beginning of the string
    const versionMatch = cleanVersion.match(/^(\d+\.\d+\.\d+)/);

    return versionMatch ? versionMatch[1] : cleanVersion;
  }

  public inject<T>(
    token: Type<T> | InjectionToken<T> | AbstractType<T>,
    notFoundValue?: T,
  ): T {
    if (isFunction(this.injector.get)) {
      return this.injector?.get(token, notFoundValue);
    }

    return notFoundValue;
  }

  public getAppInfo() {
    const appName = get(environment, 'CROWDIN_APP_NAME', SupportedAppCodes.XEA);
    const appVersion = get(environment, 'version');

    return {
      appName: upperCase(appName),
      // use this to remove BUILD- prefix if exists(local or branch build)
      // and extract version from git tag format like 3.10.233-19-g68f035a
      appVersion: this.extractVersionNumber(appVersion),
    };
  }

  public getLocals(): ILocales {
    return this.localeService.getLocale();
  }

  public getDictionaryParams(params?: {
    locale?: string;
    skipFetchingDefaultLanguage?: boolean;
    skipCustomCompanyLanguage?: boolean;
    skipUntranslatedStrings?: boolean;
  }) {
    const defaultParams = {
      locale: DEFAULT_LOCALE,
      skipFetchingDefaultLanguage: true,
      skipCustomCompanyLanguage: false,
      skipUntranslatedStrings: true,
    };

    const dictionaryParams = merge({}, defaultParams, params);

    const crowdinConfig: ICrowdinConfig = get(
      this.dmDataService.companyPreferences,
      STATE_KEYS.XDM_CROWDIN_CUSTOM,
    );

    let customFileName: string;

    if (
      !isEmpty(crowdinConfig?.crowdin_language_code) &&
      !dictionaryParams.skipCustomCompanyLanguage
    ) {
      customFileName = toLower(
        `${crowdinConfig.crowdin_language_code}.${dictionaryParams.locale}.json`,
      );

      // skip untranslated strings only when we have a custom company language
      set(dictionaryParams, 'skipUntranslatedStrings', false);
    } else {
      customFileName = toLower(`default.en-us.json`);
    }

    set(dictionaryParams, 'customFileName', customFileName);

    return dictionaryParams;
  }

  public getTranslationDictionary(userParams?: {
    locale?: string;
    appName?: string;
    appVersion?: string;
    skipFetchingDefaultLanguage?: boolean;
    skipCustomCompanyLanguage?: boolean;
    skipUntranslatedStrings?: boolean;
  }) {
    const dictionaryParams = this.getDictionaryParams(
      omit(userParams, ['appVersion', 'appName']),
    );

    const defaultAppInfo = this.getAppInfo();

    const appName = get(userParams, 'appName', defaultAppInfo.appName);
    const appVersion = get(userParams, 'appVersion', defaultAppInfo.appVersion);

    this.loadedLocales.push(dictionaryParams.locale);

    return this.dmDataService
      .getDictionaryCode(
        appName,
        appVersion,
        dictionaryParams as unknown as IFilterQuery,
      )
      .pipe(
        this.catchHttpError(() => {
          remove(
            this.loadedLocales,
            (locale) => locale === dictionaryParams.locale,
          );
        }),
      );
  }

  /**
   * Method which creates HttpParams and returns
   */
  public createParams(query: IFilterQuery = {}): HttpParams {
    return createRequestParams({
      ...query,
    });
  }

  public initTranslation(): void {
    this.xprtDataService
      .fetchSiteDetail({
        select: ['id', 'store_number', 'preferred_language'],
      })
      .pipe(
        rx.map((site) => {
          const preferredLanguage = get(
            site,
            'preferred_language',
            DEFAULT_LOCALE,
          );

          // Store locale in LocaleService instead of this.locale
          this.localeService.setLocale({
            userLocale: preferredLanguage,
          });
        }),
        rx.switchMap(() => {
          const currentLocale = this.localeService.userLocale;
          return this.getTranslationDictionary({
            locale: currentLocale,
          });
        }),
        rx.catchError(() => of({})),
      )
      .subscribe((res) => {
        const currentLocale = this.localeService.userLocale;
        // Cast res to any to handle compatibility with ngx-translate 17
        this.translateService.setTranslation(currentLocale, res as any);
        this.translateService.use(currentLocale);
        this.translationInitialized$.next(true);
      });
  }
}
